import React from 'react'

interface MathLeetLogoProps {
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const MathLeetLogo: React.FC<MathLeetLogoProps> = ({ className = '', size = 'md' }) => {
  const sizes = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-20 w-20'
  }

  return (
    <svg
      className={`${sizes[size]} ${className}`}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Background Circle with Gradient */}
      <defs>
        <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" />
          <stop offset="50%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#EC4899" />
        </linearGradient>
        <linearGradient id="innerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60A5FA" />
          <stop offset="100%" stopColor="#A78BFA" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Outer Ring */}
      <circle
        cx="50"
        cy="50"
        r="48"
        stroke="url(#logoGradient)"
        strokeWidth="2"
        fill="none"
        opacity="0.8"
      />

      {/* Inner Circle Background */}
      <circle
        cx="50"
        cy="50"
        r="44"
        fill="#0F172A"
        opacity="0.95"
      />

      {/* Mathematical Infinity Symbol (∞) integrated with ML */}
      <g filter="url(#glow)">
        {/* M Shape (Left side of infinity) */}
        <path
          d="M 20 50 Q 30 35, 35 50 T 45 50"
          stroke="url(#logoGradient)"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />
        
        {/* L Shape (Right side of infinity) */}
        <path
          d="M 55 50 Q 65 65, 70 50 T 80 50"
          stroke="url(#logoGradient)"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
        />

        {/* Center Connection (completing infinity) */}
        <path
          d="M 45 50 Q 50 40, 55 50 Q 50 60, 45 50"
          stroke="url(#innerGradient)"
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
        />

        {/* Mathematical Symbols as Decorative Elements */}
        <text
          x="50"
          y="30"
          fontSize="12"
          fill="url(#logoGradient)"
          textAnchor="middle"
          opacity="0.6"
          fontWeight="bold"
        >
          ∑
        </text>
        
        <text
          x="25"
          y="70"
          fontSize="10"
          fill="url(#innerGradient)"
          textAnchor="middle"
          opacity="0.5"
          fontWeight="bold"
        >
          π
        </text>
        
        <text
          x="75"
          y="70"
          fontSize="10"
          fill="url(#innerGradient)"
          textAnchor="middle"
          opacity="0.5"
          fontWeight="bold"
        >
          ∫
        </text>
      </g>

      {/* Orbital Dots */}
      <circle cx="35" cy="50" r="2" fill="url(#logoGradient)" opacity="0.8">
        <animate
          attributeName="opacity"
          values="0.8;1;0.8"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="65" cy="50" r="2" fill="url(#logoGradient)" opacity="0.8">
        <animate
          attributeName="opacity"
          values="0.8;1;0.8"
          dur="2s"
          begin="1s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  )
}

export default MathLeetLogo